import React from 'react'
import {
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Tooltip,
  Typography
} from 'antd'
import { QuestionCircleOutlined } from '@ant-design/icons'
import InputMask from 'react-input-mask'
import { adminEmployeesApi } from 'api/adminEmployees'
import { EAdminEmployeeRole, IAdminEmployee } from 'models/adminEmployees'
import {
  ESellerEmployeeStatus,
  IConfirmOwnershipTransferRequest
} from 'models/sellerEmployees'
import {
  getContactCodeCooldownSeconds,
  setContactCodeCooldown
} from 'shared/utils/contactCodeCooldown'
import { normalizePhone } from 'shared/utils/normalizePhone'
import { phoneMaskStatic } from 'shared/utils/phoneMaskStatic'
import { showMessage } from 'shared/utils/showMessage'
import { showServerError } from 'shared/utils/showServerError'
import { ContactVerificationBadge } from 'components/ui/ContactVerificationBadge/ContactVerificationBadge'
import { PhoneVerificationModal } from 'components/ui/PhoneVerificationModal/PhoneVerificationModal'
import { OwnershipTransferModal } from 'components/employees/OwnershipTransferModal/OwnershipTransferModal'
import { AdminAccessLog } from './AdminAccessLog'
import styles from './Employees.module.scss'

interface IAdminEmployeeForm {
  name: string
  surname: string
  email: string
  phone?: string
}

const PHONE_HINT =
  'Код придёт по SMS на этот номер — его называет вам администратор. После подтверждения администратор входит в панель по номеру телефона. Как только код отправлен, прежний номер отвязывается. Номер, подтверждённый в другом аккаунте, привязать нельзя.'

interface IAdminEmployeeModalProps {
  open: boolean
  // null — приглашение нового администратора
  employee: IAdminEmployee | null
  // править и увольнять может только супер-админ; остальным карточка открыта
  // ради журнала входов и блокировки
  canManage: boolean
  onClose: () => void
  onSaved: (employee: IAdminEmployee) => void
  // Телефон меняется без закрытия окна: строку списка обновляем сразу
  onContactsChanged: (employee: IAdminEmployee) => void
  onFired: (employee: IAdminEmployee) => void
  onRevoked: (id: string) => void
  onOwnershipTransferred: () => void
}

export const AdminEmployeeModal: React.FC<IAdminEmployeeModalProps> = ({
  open,
  employee,
  canManage,
  onClose,
  onSaved,
  onContactsChanged,
  onFired,
  onRevoked,
  onOwnershipTransferred
}) => {
  const [form] = Form.useForm<IAdminEmployeeForm>()
  const [isSaving, setIsSaving] = React.useState(false)
  const [isActing, setIsActing] = React.useState(false)
  const [isTransferOpen, setIsTransferOpen] = React.useState(false)
  const [savedPhone, setSavedPhone] = React.useState(0)
  const [isPhoneVerified, setIsPhoneVerified] = React.useState(false)
  const [isSendingPhoneCode, setIsSendingPhoneCode] = React.useState(false)
  const [isPhoneModalOpen, setIsPhoneModalOpen] = React.useState(false)
  const isEdit = Boolean(employee)
  // имя и телефон — в аккаунте, он появляется, когда приглашение примут
  const hasAccount = Boolean(employee?.user_id)
  // уволенного открывают ради журнала входов и чёрного списка, править его нечего
  const isFired = employee?.status === ESellerEmployeeStatus.FIRED
  const isReadOnly = isFired || !canManage
  const fullName = employee ? `${employee.name} ${employee.surname}`.trim() : ''

  const phone: string | undefined = Form.useWatch('phone', form)
  const currentPhone = normalizePhone(phone ?? '')
  const isPhoneConfirmed = isPhoneVerified && currentPhone === savedPhone
  // Пауза между кодами на сервере — у аккаунта, а код уходит на конкретный номер
  const phoneCooldownKey = `admin-employee:${employee?._id}:${currentPhone}`

  React.useEffect(() => {
    if (!open) return
    form.resetFields()
    setSavedPhone(employee?.tel ?? 0)
    setIsPhoneVerified(Boolean(employee?.is_phone_verified))
    if (employee) {
      const { name, surname, email, tel } = employee
      form.setFieldsValue({
        name,
        surname,
        email,
        phone: tel ? phoneMaskStatic(tel) : undefined
      })
    }
  }, [open, employee, form])

  const requestPhoneCode = async (id: string, newPhone: number) => {
    const { data } = await adminEmployeesApi.requestPhoneCode(id, newPhone)
    return data
  }

  const confirmPhone = async () => {
    if (!employee) return

    if (!/^79\d{9}$/.test(String(currentPhone))) {
      showMessage(
        'error',
        'Некорректный номер телефона',
        'Введите мобильный номер в формате +7 (9XX) XXX-XX-XX'
      )
      return
    }

    try {
      setIsSendingPhoneCode(true)
      // код живёт 10 минут, а отправлять его можно раз в минуту: внутри паузы
      // сразу открываем ввод уже отправленного кода
      if (getContactCodeCooldownSeconds(phoneCooldownKey) === 0) {
        const data = await requestPhoneCode(employee._id, currentPhone)
        const isAlreadyVerified = Boolean(data.is_phone_verified)
        // сервер записывает номер в аккаунт уже при отправке кода
        setSavedPhone(currentPhone)
        setIsPhoneVerified(isAlreadyVerified)
        onContactsChanged({
          ...employee,
          tel: currentPhone,
          is_phone_verified: isAlreadyVerified
        })
        if (isAlreadyVerified) {
          showMessage('success', 'Телефон уже подтверждён', '')
          return
        }
        setContactCodeCooldown(phoneCooldownKey)
      }
      setIsPhoneModalOpen(true)
    } catch (e) {
      showServerError(e)
    } finally {
      setIsSendingPhoneCode(false)
    }
  }

  const confirmPhoneCode = async (code: number) => {
    if (!employee) return
    const { data } = await adminEmployeesApi.verifyPhoneCode(employee._id, code)
    setSavedPhone(data.tel ?? 0)
    setIsPhoneVerified(data.is_phone_verified)
    onContactsChanged(data)
  }

  const onSubmit = async () => {
    let values: IAdminEmployeeForm
    try {
      values = await form.validateFields()
    } catch {
      // ошибки уже показаны под полями
      return
    }

    if (employee?.user_id && currentPhone !== savedPhone) {
      showMessage(
        'error',
        'Номер телефона не подтверждён',
        'Нажмите «Подтвердить» у номера или верните прежний номер'
      )
      return
    }

    setIsSaving(true)
    try {
      if (employee) {
        const { data } = await adminEmployeesApi.updateEmployee(employee._id, {
          name: values.name,
          surname: values.surname?.trim() || undefined
        })
        showMessage('success', 'Изменения сохранены', '')
        onSaved(data)
      } else {
        const { data } = await adminEmployeesApi.inviteEmployee({
          email: values.email
        })
        showMessage(
          'success',
          'Администратор приглашён',
          `Письмо со ссылкой-приглашением отправлено на ${data.email}`
        )
        onSaved(data)
      }
    } catch (e) {
      showServerError(e)
    } finally {
      setIsSaving(false)
    }
  }

  const runAction = async (action: () => Promise<void>) => {
    try {
      setIsActing(true)
      await action()
    } catch (e) {
      showServerError(e)
    } finally {
      setIsActing(false)
    }
  }

  const fireEmployee = (id: string) =>
    runAction(async () => {
      const { data } = await adminEmployeesApi.fireEmployee(id)
      showMessage(
        'success',
        'Администратор уволен',
        'Доступ к панели администратора закрыт'
      )
      onFired(data)
    })

  const resendInvitation = (id: string) =>
    runAction(async () => {
      const { data } = await adminEmployeesApi.resendInvitation(id)
      showMessage('success', 'Приглашение отправлено повторно', '')
      onSaved(data)
    })

  const revokeInvitation = (id: string) =>
    runAction(async () => {
      await adminEmployeesApi.revokeInvitation(id)
      showMessage(
        'success',
        'Приглашение отозвано',
        'Ссылка из письма больше не действует'
      )
      onRevoked(id)
    })

  const confirmTransfer = async (codes: IConfirmOwnershipTransferRequest) => {
    if (!employee) return
    await adminEmployeesApi.confirmOwnershipTransfer(employee._id, codes)
    showMessage(
      'success',
      'Роль передана',
      `Супер-админ теперь ${fullName}. Вы остаётесь администратором`
    )
    setIsTransferOpen(false)
    onOwnershipTransferred()
  }

  const renderManageActions = () => {
    if (!employee) return null

    if (employee.status === ESellerEmployeeStatus.INVITED) {
      return (
        <>
          <Popconfirm
            title='Отозвать приглашение?'
            description='Ссылка из письма перестанет работать.'
            okText='Отозвать'
            cancelText='Отмена'
            okButtonProps={{ danger: true }}
            onConfirm={() => revokeInvitation(employee._id)}
          >
            <Button danger disabled={isActing}>
              Отозвать приглашение
            </Button>
          </Popconfirm>
          <Button
            disabled={isActing}
            onClick={() => resendInvitation(employee._id)}
          >
            Отправить повторно
          </Button>
        </>
      )
    }

    if (
      employee.status !== ESellerEmployeeStatus.ACTIVE ||
      employee.role !== EAdminEmployeeRole.ADMIN
    ) {
      return null
    }

    return (
      <>
        <Popconfirm
          title='Уволить администратора?'
          description='Доступ к панели закроется сразу, аккаунт станет обычным покупателем. Адреса и браузеры, с которых он входил, попадут в чёрный список.'
          okText='Уволить'
          cancelText='Отмена'
          okButtonProps={{ danger: true }}
          onConfirm={() => fireEmployee(employee._id)}
        >
          <Button danger disabled={isActing}>
            Уволить
          </Button>
        </Popconfirm>
        {employee.user_id && (
          <Button disabled={isActing} onClick={() => setIsTransferOpen(true)}>
            Передать роль
          </Button>
        )}
      </>
    )
  }

  return (
    <>
      <Modal
        centered
        open={open}
        width={hasAccount ? 760 : undefined}
        title={isEdit ? 'Администратор' : 'Пригласить администратора'}
        onCancel={onClose}
        forceRender
        footer={
          <div className={styles.modalFooter}>
            <div className={styles.modalFooterGroup}>
              {canManage && renderManageActions()}
            </div>
            <div className={styles.modalFooterGroup}>
              <Button onClick={onClose}>
                {isReadOnly ? 'Закрыть' : 'Отмена'}
              </Button>
              {(!employee || hasAccount) && !isReadOnly && (
                <Button type='primary' loading={isSaving} onClick={onSubmit}>
                  {isEdit ? 'Сохранить' : 'Пригласить'}
                </Button>
              )}
            </div>
          </div>
        }
      >
        <Form
          form={form}
          layout='vertical'
          requiredMark={false}
          disabled={isReadOnly}
        >
          {hasAccount && (
            <div className={styles.nameRow}>
              <Form.Item
                name='name'
                label='Имя'
                rules={[
                  { required: true, whitespace: true, message: 'Укажите имя' }
                ]}
              >
                <Input maxLength={100} />
              </Form.Item>
              <Form.Item name='surname' label='Фамилия'>
                <Input maxLength={100} />
              </Form.Item>
            </div>
          )}
          <Form.Item
            name='email'
            label={
              <span className={styles.labelWithHelp}>
                Почта
                {!isEdit && (
                  <Tooltip title='На почту придёт письмо со ссылкой-приглашением, она действует 7 дней. По ссылке администратор указывает имя, задаёт пароль и подтверждает телефон по SMS, после этого входит в панель под этой почтой. Аккаунт с этой почтой не должен владеть магазином или работать в нём сотрудником.'>
                    <QuestionCircleOutlined className={styles.helpIcon} />
                  </Tooltip>
                )}
              </span>
            }
            rules={[
              { required: true, message: 'Укажите почту' },
              { type: 'email', message: 'Некорректная почта' }
            ]}
          >
            <Input
              disabled={isEdit}
              inputMode='email'
              autoComplete='off'
              suffix={
                employee ? (
                  <ContactVerificationBadge
                    isVerified={employee.is_email_verified}
                  />
                ) : undefined
              }
            />
          </Form.Item>
          {hasAccount && (
            <Form.Item
              name='phone'
              label={
                <span className={styles.labelWithHelp}>
                  Телефон
                  <Tooltip title={PHONE_HINT}>
                    <QuestionCircleOutlined className={styles.helpIcon} />
                  </Tooltip>
                </span>
              }
            >
              <InputMask mask='+7 (999) 999-99-99' maskChar={null}>
                {/* @ts-ignore */}
                {() => (
                  <Input
                    placeholder='Введите номер телефона'
                    suffix={
                      <ContactVerificationBadge
                        isVerified={isPhoneConfirmed}
                        isLoading={isSendingPhoneCode}
                        onConfirm={isReadOnly ? undefined : confirmPhone}
                      />
                    }
                  />
                )}
              </InputMask>
            </Form.Item>
          )}
        </Form>
        {open && employee?.user_id && (
          <AdminAccessLog employeeId={employee._id} />
        )}
      </Modal>
      <PhoneVerificationModal
        isOpen={isPhoneModalOpen}
        phone={currentPhone}
        cooldownKey={phoneCooldownKey}
        requestCode={(newPhone) =>
          requestPhoneCode(employee?._id ?? '', newPhone)
        }
        confirmCode={confirmPhoneCode}
        onClose={() => setIsPhoneModalOpen(false)}
      />
      <OwnershipTransferModal
        open={isTransferOpen}
        title='Передать роль супер-админа'
        confirmText='Передать роль'
        failText='Не удалось передать роль'
        warning={
          <>
            <Typography.Text>
              Супер-админом станет {fullName}. Вы останетесь администратором, а
              приглашать и увольнять администраторов и передавать роль будет
              новый супер-админ. Вернуть роль сможет только он.
            </Typography.Text>
            <Typography.Text>
              Для подтверждения пришлём два кода: по SMS на ваш телефон и на вашу
              почту.
            </Typography.Text>
          </>
        }
        requestCodes={async () => {
          const { data } = await adminEmployeesApi.requestOwnershipTransfer(
            employee?._id ?? ''
          )
          return data
        }}
        confirmCodes={confirmTransfer}
        onClose={() => setIsTransferOpen(false)}
      />
    </>
  )
}
